import { useLayout } from '@/providers'
import Logo from '@components/Logo/Logo'
import LogoIcon from '@components/LogoIcon/LogoIcon'
import PermissionsGuard, { Permissions } from '@components/PermissionsGuard/PermissionsGuard'
import {
	faArrowRightFromBracket,
	faBasketShopping,
	faChartMixed,
	faChevronsLeft,
	faLightbulbGear,
	faServer,
	faShuffle,
	faUser,
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import useLogout from '@hooks/useLogout'
import useUpdatePreferences from '@hooks/useUpdatePreferences'
import { useBoolVariation } from '@launchdarkly/react-sdk'
import { Box, Button, Divider, List, Stack, useMediaQuery, useTheme } from '@mui/material'
import { blueGrey } from '@mui/material/colors'
import { Link, useLocation } from 'react-router-dom'

import Help from './components/Help'
import NavButton from './components/NavButton'
import ProfileSettings from './components/ProfileSettings'

export default function SidebarContent() {
	const { sidebarExpanded, sidebarWidth, setSidebarExpanded } = useLayout()
	const theme = useTheme()
	const largeScreen = useMediaQuery(theme.breakpoints.up('sm'))
	const { pathname } = useLocation()
	const url = removeQueryParameters(pathname)
	const isAnalyticsRoute = url.startsWith('/app/market-baskets/analytics')
	const isMarketBasketsRoute = url.startsWith('/app/market-baskets') && !isAnalyticsRoute
	const isTaskServerRoute = url.startsWith('/app/task-server')
	const switchTrackingEnabled = useBoolVariation('feature-art-1567-switch-tracking', false)

	const { mutate: logout } = useLogout()
	const { mutate: updateSidebarPref, isPending } = useUpdatePreferences()

	const handleLogout = () => {
		logout()
	}

	const handleChangeSidebarSize = (e) => {
		// prevent closing of temp sidebar on click
		e.stopPropagation()
		setSidebarExpanded((prev) => !prev)
		// Sidebar grow/shrink animation sluggish without delay for api call
		setTimeout(() => {
			updateSidebarPref({
				userPreferences: [{ key: 'sidebarExpanded', value: (!sidebarExpanded).toString() }],
			})
		}, 500)
	}

	return (
		<>
			<Stack
				pt="1.5rem"
				pb="1rem"
				alignItems="center"
				justifyContent="center"
				height="4.5rem"
				width={sidebarWidth}
				sx={{ overflow: 'hidden' }}
			>
				<Link
					to="/app"
					data-testid="sidebar-logo"
					style={{ alignItems: 'center', justifyContent: 'center', display: 'flex', width: '100%' }}
				>
					{sidebarExpanded ? (
						<Logo style={{ width: '60%' }} />
					) : (
						<Box height="2rem" width="2rem">
							<LogoIcon />
						</Box>
					)}
				</Link>
			</Stack>
			<Divider variant="middle" />
			<Stack sx={{ width: '100%' }} height="100%" justifyContent="space-between">
				<List sx={{ height: '100%', paddingY: '1rem' }} component="nav">
					<Stack sx={{ height: '100%' }} justifyContent="space-between">
						<Stack gap={1}>
							<PermissionsGuard
								disableNotAuthorized
								showWhenHasPermission={[Permissions.BasketEdit]}
							>
								<NavButton
									icon={
										<FontAwesomeIcon
											style={{ height: '1.5rem' }}
											size="xl"
											icon={faBasketShopping}
										/>
									}
									to="/app/market-baskets"
									selected={isMarketBasketsRoute}
									primary="Market Baskets"
									data-testid="sidebar-market-basket"
								/>
							</PermissionsGuard>
							{/* <NavButton
								selected={pathname.includes('/app/reports')}
								to="/app/reports"
								icon={<FontAwesomeIcon icon={faFileChartPie} />}
								primary="Reports"
							/> */}
							<PermissionsGuard
								disableNotAuthorized
								showWhenHasPermission={[Permissions.BiDashboardView]}
							>
								<NavButton
									selected={isAnalyticsRoute}
									to="/app/market-baskets/analytics"
									icon={
										<FontAwesomeIcon style={{ height: '1.5rem' }} size="xl" icon={faChartMixed} />
									}
									primary="Analytics"
									data-testid="sidebar-market-basket-analytics"
								/>
							</PermissionsGuard>
							{switchTrackingEnabled && (
								<PermissionsGuard
									disableNotAuthorized
									showWhenHasPermission={[Permissions.SwitchTracking]}
								>
									<NavButton
										selected={url.startsWith('/app/switch-tracking')}
										to="/app/switch-tracking"
										icon={
											<FontAwesomeIcon style={{ height: '1.5rem' }} size="xl" icon={faShuffle} />
										}
										primary="Switch Tracking"
										data-testid="sidebar-switch-tracking"
									/>
								</PermissionsGuard>
							)}
							<PermissionsGuard
								disableNotAuthorized
								showWhenHasPermission={[Permissions.MarketBasketSettings]}
							>
								<NavButton
									selected={url.startsWith('/app/global-basket-settings')}
									to="/app/global-basket-settings"
									icon={
										<FontAwesomeIcon
											style={{ height: '1.5rem' }}
											size="xl"
											icon={faLightbulbGear}
										/>
									}
									primary="Global Basket Settings"
									data-testid="sidebar-global-basket-settings"
								/>
							</PermissionsGuard>

							<PermissionsGuard
								disableNotAuthorized
								showWhenHasPermission={[Permissions.TaskServerView]}
							>
								<NavButton
									selected={isTaskServerRoute}
									to="/app/task-server"
									icon={<FontAwesomeIcon style={{ height: '1.5rem' }} size="xl" icon={faServer} />}
									primary="Task Server"
									data-testid="sidebar-task-server"
								/>
							</PermissionsGuard>
						</Stack>
						{largeScreen && (
							<Button
								sx={{
									width: '4rem',
									height: '3rem',
									backgroundColor:
										theme.palette.mode === 'light' ? `${blueGrey[50]}` : 'secondary.dark',
									alignSelf: 'flex-end',
									mr: sidebarExpanded ? '1rem' : 0,
								}}
								disabled={isPending}
								onClick={handleChangeSidebarSize}
								data-testid="sidebar-expand-button"
							>
								<FontAwesomeIcon
									style={{
										marginLeft: !sidebarExpanded ? '0.5rem' : 0,
										transition: 'transform 0.5s',
										transform: `rotate(${!sidebarExpanded ? -180 : 0}deg)`,
									}}
									color={
										theme.palette.mode === 'light'
											? theme.palette.common.black
											: theme.palette.common.white
									}
									size="xl"
									icon={faChevronsLeft}
								/>
							</Button>
						)}
					</Stack>
				</List>
				<Stack>
					<Divider variant="middle" />
					<List sx={{ height: '100%', paddingY: '1rem' }} component="nav">
						<Stack gap={1}>
							<PermissionsGuard
								disableNotAuthorized
								showWhenHasPermission={[Permissions.ManageUsers]}
							>
								<NavButton
									to="/app/user-management"
									selected={url === '/app/user-management'}
									icon={<FontAwesomeIcon style={{ height: '1.5rem' }} size="xl" icon={faUser} />}
									primary="User Management"
									data-testid="sidebar-user-management"
								/>
							</PermissionsGuard>
							<ProfileSettings />
							<Help />
							<NavButton
								icon={
									<FontAwesomeIcon
										style={{ height: '1.5rem' }}
										size="xl"
										icon={faArrowRightFromBracket}
									/>
								}
								primary="Logout"
								onClick={handleLogout}
								data-testid="sidebar-logout"
							/>
						</Stack>
					</List>
				</Stack>
			</Stack>
		</>
	)
}

function removeQueryParameters(pathname: string) {
	const urlParts = pathname.split('?')
	return urlParts[0]
}
